// Direct mode: pages are rendered on THIS device (smooth, native scrolling, audio,
// camera…), but every request is fetched by the server through the egress proxy.
// Each tab is an iframe on the separate direct-mode origin (see client/direct/).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Cloud } from 'lucide-react';
import type { Api } from '../core/api';
import { t } from '../core/i18n';
import type { Tab } from '../core/protocol';
import { BottomBar, TabSheet, TabStrip, Toolbar } from './Chrome';
import { NewTabPage, type RecentSite } from './Overlays';
import { SettingsPage } from './SettingsPage';
import { SiteInfo } from './SiteInfo';
import { PermissionBroker } from '../core/permissions';

interface DirectTab {
  id: string;
  error?: string;
  url: string; // '' = new tab page
  title: string;
  favicon?: string;
  loading: boolean;
  frameUrl?: string; // iframe src (set once, later navigation via postMessage)
}

const TABS_KEY = 'gcb.direct.tabs';
const RECENT_KEY = 'gcb.recent';
const newId = () => Math.random().toString(36).slice(2, 10);

const load = <T,>(key: string, fallback: T): T => {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
};
const save = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
};

function useNarrow() {
  const mq = useMemo(() => matchMedia('(max-width: 640px)'), []);
  const [narrow, setNarrow] = useState(mq.matches);
  useEffect(() => {
    const h = () => setNarrow(mq.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, [mq]);
  return narrow;
}

export function DirectBrowser({ api, onLogout, onCloudMode }: { api: Api; onLogout: () => void; onCloudMode: (url?: string) => void }) {
  const restored = useMemo(() => load<{ tabs: DirectTab[]; active: string }>(TABS_KEY, { tabs: [], active: '' }), []);
  const [tabs, setTabs] = useState<DirectTab[]>(() =>
    restored.tabs.length ? restored.tabs.map((x) => ({ ...x, loading: false, frameUrl: undefined })) : [{ id: newId(), url: '', title: '', loading: false }],
  );
  const [active, setActive] = useState(restored.active && restored.tabs.some((x) => x.id === restored.active) ? restored.active : tabs[0].id);
  const [direct, setDirect] = useState<{ token: string; origin: string } | null>(null);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(false);
  const [tabSheet, setTabSheet] = useState(false);
  const [focusAddr, setFocusAddr] = useState(0);
  const [recent, setRecent] = useState<RecentSite[]>(() => load(RECENT_KEY, []));
  const frames = useRef(new Map<string, HTMLIFrameElement>());
  const narrow = useNarrow();
  const broker = useMemo(() => new PermissionBroker(api, () => undefined, () => {}), [api]);

  // --- token for the direct-mode origin
  const fetchToken = useCallback(async () => {
    try {
      const r = await api.req<{ token: string; origin: string | null; port: number }>('POST', '/api/direct/token');
      const origin = r.origin || `${location.protocol}//${location.hostname}:${r.port}`;
      setDirect({ token: r.token, origin });
      setError('');
    } catch (e: any) {
      setError(e?.message || t('errorGeneric'));
    }
  }, [api]);
  useEffect(() => {
    fetchToken();
    const id = setInterval(fetchToken, 6 * 3600_000); // tokens live 12 h
    return () => clearInterval(id);
  }, [fetchToken]);

  // --- persist tabs (this device)
  useEffect(() => {
    save(TABS_KEY, { tabs: tabs.map(({ frameUrl: _f, ...x }) => x), active });
  }, [tabs, active]);

  // --- remember recently visited sites
  useEffect(() => {
    const done = tabs.filter((x) => !x.loading && /^https?:/.test(x.url) && x.title);
    if (!done.length) return;
    setRecent((prev) => {
      let next = [...prev];
      for (const x of done) {
        next = next.filter((r) => r.url !== x.url);
        next.unshift({ url: x.url, title: x.title, favicon: x.favicon });
      }
      next = next.slice(0, 8);
      if (JSON.stringify(next) === JSON.stringify(prev)) return prev;
      save(RECENT_KEY, next);
      return next;
    });
  }, [tabs]);

  // --- messages from tab frames
  useEffect(() => {
    if (!direct) return;
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== direct.origin) return;
      const m = e.data;
      if (!m || m.gcb !== 'tab' || typeof m.id !== 'string') return;
      if (m.type === 'state') {
        setTabs((list) =>
          list.map((x) =>
            x.id === m.id
              ? {
                  ...x,
                  url: typeof m.url === 'string' ? m.url : x.url,
                  title: typeof m.title === 'string' ? m.title.slice(0, 300) : x.title,
                  favicon:
                    typeof m.favicon !== 'string' || !m.favicon
                      ? x.favicon
                      : m.favicon.startsWith('data:image/')
                        ? m.favicon
                        : /^https?:\/\//.test(m.favicon)
                          ? api.url('/api/direct/icon?url=' + encodeURIComponent(m.favicon))
                          : x.favicon,
                  loading: !!m.loading,
                }
              : x,
          ),
        );
      } else if (m.type === 'error') {
        setTabs((list) => list.map((x) => (x.id === m.id ? { ...x, loading: false, error: typeof m.message === 'string' ? m.message.slice(0, 600) : t('errorGeneric') } : x)));
      } else if (m.type === 'ready') {
        setTabs((list) => list.map((x) => (x.id === m.id ? { ...x, error: undefined } : x)));
      }
    };
    addEventListener('message', onMsg);
    return () => removeEventListener('message', onMsg);
  }, [direct]);

  const frameSrc = (tab: DirectTab, url: string) => {
    if (!direct) return undefined;
    const h = new URLSearchParams({ t: direct.token, o: location.origin, id: tab.id, u: url });
    return `${direct.origin}/tab.html#${h}`;
  };

  const send = (id: string, msg: Record<string, unknown>) => frames.current.get(id)?.contentWindow?.postMessage({ gcb: 'cmd', ...msg }, direct?.origin ?? '*');

  const navigate = (url: string, tabId = active) => {
    if (!/^https?:\/\//i.test(url)) return;
    setTabs((list) =>
      list.map((x) => {
        if (x.id !== tabId) return x;
        if (!x.frameUrl) return { ...x, url, title: '', favicon: undefined, loading: true, frameUrl: frameSrc(x, url) };
        send(x.id, { cmd: 'go', url });
        return { ...x, url, loading: true };
      }),
    );
  };

  // restore: (re)create frames for tabs that had a URL once the token is there
  useEffect(() => {
    if (!direct) return;
    setTabs((list) => list.map((x) => (x.url && !x.frameUrl ? { ...x, frameUrl: frameSrc(x, x.url) } : x)));
  }, [direct]);

  const newTab = (url = '') => {
    const tab: DirectTab = { id: newId(), url: '', title: '', loading: false };
    setTabs((list) => [...list, tab]);
    setActive(tab.id);
    if (url) setTimeout(() => navigate(url, tab.id));
    else setFocusAddr((n) => n + 1);
  };

  const closeTab = (id: string) => {
    setTabs((list) => {
      const idx = list.findIndex((x) => x.id === id);
      const next = list.filter((x) => x.id !== id);
      frames.current.delete(id);
      if (!next.length) {
        const fresh = { id: newId(), url: '', title: '', loading: false };
        setActive(fresh.id);
        return [fresh];
      }
      if (id === active) setActive(next[Math.min(idx, next.length - 1)].id);
      return next;
    });
  };

  // browser-like shortcuts while focus is in the app chrome
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'l') {
        e.preventDefault();
        setFocusAddr((n) => n + 1);
      } else if (mod && e.key.toLowerCase() === 't') {
        e.preventDefault();
        newTab();
      } else if (mod && e.key.toLowerCase() === 'w') {
        e.preventDefault();
        closeTab(active);
      }
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  });

  const current = tabs.find((x) => x.id === active) ?? tabs[0];
  const asTab = (x: DirectTab): Tab => ({
    id: x.id,
    url: x.url,
    title: x.title || (x.url ? new URL(x.url).host : ''),
    favicon: x.favicon,
    loading: x.loading,
    canGoBack: !!x.frameUrl,
    canGoForward: !!x.frameUrl,
  });
  const uiTabs = tabs.map(asTab);

  const toolbarProps = {
    tab: current ? asTab(current) : undefined,
    focusSignal: focusAddr,
    muted: false,
    activeMedia: [],
    onNavigate: (url: string) => navigate(url),
    onBack: () => send(active, { cmd: 'back' }),
    onForward: () => send(active, { cmd: 'forward' }),
    onReload: () => send(active, { cmd: 'reload' }),
    onStop: () => send(active, { cmd: 'stop' }),
    onToggleMute: () => {},
    onStopMedia: () => {},
    onSettings: () => setSettings(true),
    onLogout,
    extraItems: [
      {
        label: t('openInCloud'),
        icon: Cloud,
        onClick: () => onCloudMode(current?.url && /^https?:/.test(current.url) ? current.url : undefined),
      },
    ],
    siteInfo: (
      <SiteInfo api={api} broker={broker} url={current?.url ?? ''} canClearCookies={false} onSettings={() => setSettings(true)} onToast={() => {}} up={narrow} />
    ),
  };

  return (
    <div className="app">
      {!narrow && (
        <header className="chrome">
          <TabStrip tabs={uiTabs} active={active} muted={false} onActivate={setActive} onClose={closeTab} onNew={() => newTab()} onUnmute={() => {}} />
          <Toolbar {...toolbarProps} />
        </header>
      )}
      {narrow && <div style={{ height: 'var(--safe-top)', background: 'var(--bar)', flex: 'none' }} />}

      <main className="viewport-host">
        {tabs.map((x) =>
          x.frameUrl ? (
            <iframe
              key={x.id}
              ref={(el) => {
                if (el) frames.current.set(x.id, el);
              }}
              className="direct-frame"
              src={x.frameUrl}
              title={x.title || x.url}
              hidden={x.id !== active}
              allow="camera *; microphone *; geolocation *; clipboard-read *; clipboard-write *; display-capture *; fullscreen *; autoplay *; encrypted-media *; picture-in-picture *"
              allowFullScreen
            />
          ) : null,
        )}
        {current && !current.frameUrl && <NewTabPage recent={recent} onNavigate={(u) => navigate(u)} />}
        {current?.error && (
          <div className="blank-state column direct-error">
            <strong>{t('directFailed')}</strong>
            <span className="muted">{current.error}</span>
            <div className="actions-row">
              <button className="btn" onClick={() => location.reload()}>
                {t('retry')}
              </button>
              <button className="btn primary" onClick={() => onCloudMode(current.url && /^https?:/.test(current.url) ? current.url : undefined)}>
                {t('openInCloudShort')}
              </button>
            </div>
          </div>
        )}
        {error && (
          <div className="blank-state column">
            <span>{error}</span>
            <button className="btn" onClick={fetchToken}>
              {t('retry')}
            </button>
          </div>
        )}
      </main>

      {narrow && <BottomBar {...toolbarProps} tabCount={tabs.length} onTabs={() => setTabSheet(true)} onKeyboard={() => setFocusAddr((n) => n + 1)} />}
      {tabSheet && <TabSheet tabs={uiTabs} active={active} onActivate={setActive} onClose={closeTab} onNew={() => newTab()} onDone={() => setTabSheet(false)} />}
      {settings && <SettingsPage api={api} broker={broker} onClose={() => setSettings(false)} onEndSession={() => setSettings(false)} onLogout={onLogout} />}
    </div>
  );
}
